import type { ReactNode } from 'react';
import {
  CheckCircle2,
  ImageIcon,
  Network,
  Terminal,
  AlertTriangle,
  XCircle,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

type NetworkIssue = {
  url?: string;
  resource_type?: string;
  status_before?: number;
  status_after?: number;
};

type ScreenshotDiff = {
  flagged?: boolean;
  percent_pixels_changed?: number | null;
  page_height_or_width_changed?: boolean;
  threshold_percent?: number | null;
  diff_image_api?: string | null;
};

type NetworkDiff = {
  flagged?: boolean;
  broken_after?: NetworkIssue[];
  missing_after?: NetworkIssue[];
};

type ConsoleDiff = {
  flagged?: boolean;
  new_errors_or_warnings?: string[];
};

export type ComparisonPayload = {
  comparison?: {
    result?: string | null;
    hasDifferences?: boolean | null;
    screenshotDiff?: ScreenshotDiff | null;
    networkDiff?: NetworkDiff | null;
    consoleDiff?: ConsoleDiff | null;
    comparedAt?: string | null;
    report?: {
      overall_result?: string;
      screenshot?: ScreenshotDiff | null;
    } | null;
  } | null;
  captureId?: string;
};

interface ComparisonDiffModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  data: ComparisonPayload | null;
  title?: string;
}

function StatusChip({ ok }: { ok: boolean }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full border border-solid px-2.5 py-0.5 text-[0.65rem] font-bold uppercase tracking-[0.06em]',
        ok
          ? 'border-[var(--inv-border)] bg-[var(--inv-accent-soft)] text-[var(--inv-accent-hover)]'
          : 'border-[#f5c4be] bg-[#fff1f0] text-[#b42318]',
      )}>
      {ok ? (
        <CheckCircle2 className="h-3 w-3" aria-hidden />
      ) : (
        <AlertTriangle className="h-3 w-3" aria-hidden />
      )}
      {ok ? 'Match' : 'Diff'}
    </span>
  );
}

function MetricTile({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <div className="min-w-0 rounded-[12px] border border-solid border-[var(--inv-border)] bg-[var(--inv-surface)] px-3 py-2.5 shadow-[var(--inv-shadow)]">
      <span className="mb-1 block text-[0.65rem] font-bold uppercase tracking-[0.08em] text-[var(--inv-text-muted)]">
        {label}
      </span>
      <span className="block text-[1.15rem] font-bold leading-tight tabular-nums text-[var(--inv-text)]">
        {value}
      </span>
      {hint ? (
        <span className="mt-0.5 block text-[0.7rem] text-[var(--inv-text-secondary)]">
          {hint}
        </span>
      ) : null}
    </div>
  );
}

function CheckCard({
  icon,
  title,
  ok,
  summary,
  children,
}: {
  icon: ReactNode;
  title: string;
  ok: boolean;
  summary: string;
  children?: ReactNode;
}) {
  return (
    <section
      className={cn(
        'rounded-[14px] border border-solid bg-[var(--inv-surface)] p-4 shadow-[var(--inv-shadow)]',
        ok
          ? 'border-[var(--inv-border)] border-l-[3px] border-l-[var(--inv-accent)]'
          : 'border-[#f5c4be] border-l-[3px] border-l-[#d92d20]',
      )}>
      <div className="mb-2 flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2.5">
          <span
            className={cn(
              'flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px]',
              ok
                ? 'bg-[var(--inv-accent-soft)] text-[var(--inv-accent)]'
                : 'bg-[#fff1f0] text-[#d92d20]',
            )}>
            {icon}
          </span>
          <div className="min-w-0">
            <h3 className="m-0 font-serif text-[1.02rem] font-semibold text-[var(--inv-text)]">
              {title}
            </h3>
            <p className="m-0 mt-0.5 text-[0.78rem] leading-snug text-[var(--inv-text-secondary)]">
              {summary}
            </p>
          </div>
        </div>
        <StatusChip ok={ok} />
      </div>
      {children ? <div className="mt-3">{children}</div> : null}
    </section>
  );
}

function IssueList({
  items,
  emptyLabel,
}: {
  items: ReactNode[];
  emptyLabel: string;
}) {
  if (!items.length) {
    return (
      <p className="m-0 rounded-[10px] border border-dashed border-[var(--inv-border-soft)] bg-[var(--inv-surface-muted)] px-3 py-2 text-[0.75rem] text-[var(--inv-text-muted)]">
        {emptyLabel}
      </p>
    );
  }
  return (
    <ul className="m-0 max-h-32 list-none space-y-1.5 overflow-auto p-0">
      {items.map((item, idx) => (
        <li
          key={idx}
          className="break-all rounded-[10px] border border-solid border-[var(--inv-border-soft)] bg-[var(--inv-surface-muted)] px-2.5 py-1.5 text-[0.75rem] leading-snug text-[var(--inv-text-body)]">
          {item}
        </li>
      ))}
    </ul>
  );
}

const ComparisonDiffModal = ({
  open,
  onOpenChange,
  data,
  title = 'Review Differences',
}: ComparisonDiffModalProps) => {
  const comparison = data?.comparison || {};
  const screenshot =
    comparison.screenshotDiff || comparison.report?.screenshot || null;
  const network = comparison.networkDiff || null;
  const consoleDiff = comparison.consoleDiff || null;
  const broken = network?.broken_after || [];
  const missing = network?.missing_after || [];
  const consoleIssues = consoleDiff?.new_errors_or_warnings || [];
  const result = (
    comparison.result ||
    comparison.report?.overall_result ||
    ''
  ).toUpperCase();
  const hasDiffs = Boolean(
    comparison.hasDifferences ||
      screenshot?.flagged ||
      network?.flagged ||
      consoleDiff?.flagged ||
      result === 'FAIL',
  );
  const passed = !hasDiffs && result !== 'FAIL';
  const diffImageSrc = screenshot?.diff_image_api || null;
  const pixels =
    screenshot?.percent_pixels_changed != null
      ? `${screenshot.percent_pixels_changed}%`
      : '—';
  const threshold =
    screenshot?.threshold_percent != null
      ? `${screenshot.threshold_percent}%`
      : '1%';
  const networkIssueCount = broken.length + missing.length;
  const consoleIssueCount = consoleIssues.length;
  const checksPassed =
    Number(!screenshot?.flagged) +
    Number(!network?.flagged) +
    Number(!consoleDiff?.flagged);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          'max-h-[88vh] max-w-[min(760px,94vw)] gap-0 overflow-hidden p-0',
          'border border-solid border-[var(--inv-border)] bg-[var(--inv-bg)] shadow-[var(--inv-shadow-hover)]',
        )}>
        {/* Hero header */}
        <div
          className={cn(
            'relative border-b border-solid px-6 pb-5 pt-6',
            passed
              ? 'border-[var(--inv-border-soft)] bg-[linear-gradient(165deg,var(--inv-accent-soft)_0%,var(--inv-surface)_55%,var(--inv-bg)_100%)]'
              : 'border-[#f5c4be] bg-[linear-gradient(165deg,#fff1f0_0%,var(--inv-surface)_55%,var(--inv-bg)_100%)]',
          )}>
          <DialogHeader className="space-y-2 pr-8">
            <div className="flex flex-wrap items-center gap-2">
              <span
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-full border border-solid px-2.5 py-1 text-[0.68rem] font-bold uppercase tracking-[0.08em]',
                  passed
                    ? 'border-[var(--inv-accent)]/25 bg-[var(--inv-surface)] text-[var(--inv-accent-hover)]'
                    : 'border-[#f5c4be] bg-[var(--inv-surface)] text-[#b42318]',
                )}>
                {passed ? (
                  <CheckCircle2 className="h-3.5 w-3.5" aria-hidden />
                ) : (
                  <XCircle className="h-3.5 w-3.5" aria-hidden />
                )}
                {passed ? 'Passed' : 'Differences found'}
              </span>
              {data?.captureId ? (
                <span className="rounded-full border border-solid border-[var(--inv-border)] bg-[var(--inv-surface)] px-2.5 py-1 font-mono text-[0.68rem] text-[var(--inv-text-muted)]">
                  {data.captureId.slice(-8)}
                </span>
              ) : null}
            </div>
            <DialogTitle className="font-serif text-[1.35rem] font-semibold tracking-tight text-[var(--inv-text)]">
              {title}
            </DialogTitle>
            <DialogDescription className="text-[0.875rem] leading-relaxed text-[var(--inv-text-secondary)]">
              {passed
                ? 'Before and after instrumentation captures match within the configured thresholds.'
                : 'Review where before and after instrumentation captures diverge.'}
            </DialogDescription>
          </DialogHeader>

          <div className="mt-4 grid grid-cols-2 gap-2.5 sm:grid-cols-4">
            <MetricTile
              label="Overall"
              value={passed ? 'PASS' : 'FAIL'}
              hint={`${checksPassed}/3 checks clear`}
            />
            <MetricTile
              label="Pixels"
              value={pixels}
              hint={`Threshold ${threshold}`}
            />
            <MetricTile
              label="Network"
              value={String(networkIssueCount)}
              hint={networkIssueCount ? 'issues flagged' : 'critical assets OK'}
            />
            <MetricTile
              label="Console"
              value={String(consoleIssueCount)}
              hint={
                consoleIssueCount ? 'new errors/warnings' : 'no new messages'
              }
            />
          </div>
        </div>

        {/* Detail checks */}
        <div className="flex max-h-[min(52vh,480px)] flex-col gap-3 overflow-y-auto px-6 py-5">
          <CheckCard
            icon={<ImageIcon className="h-[1.1rem] w-[1.1rem]" />}
            title="Screenshot"
            ok={!screenshot?.flagged}
            summary={
              screenshot?.flagged
                ? 'Visual regression exceeded the allowed pixel threshold.'
                : 'Visual appearance is within the allowed threshold.'
            }>
            <div className="grid grid-cols-2 gap-2">
              <div className="rounded-[10px] bg-[var(--inv-surface-muted)] px-3 py-2">
                <span className="block text-[0.65rem] font-semibold uppercase tracking-wide text-[var(--inv-text-muted)]">
                  Changed
                </span>
                <span className="text-sm font-semibold tabular-nums text-[var(--inv-text)]">
                  {pixels}
                </span>
              </div>
              <div className="rounded-[10px] bg-[var(--inv-surface-muted)] px-3 py-2">
                <span className="block text-[0.65rem] font-semibold uppercase tracking-wide text-[var(--inv-text-muted)]">
                  Dimensions
                </span>
                <span className="text-sm font-semibold text-[var(--inv-text)]">
                  {screenshot?.page_height_or_width_changed
                    ? 'Changed'
                    : 'Unchanged'}
                </span>
              </div>
            </div>
            {diffImageSrc && screenshot?.flagged ? (
              <div className="mt-3 overflow-hidden rounded-[12px] border border-solid border-[var(--inv-border)] bg-[var(--inv-surface)]">
                <img
                  src={diffImageSrc}
                  alt="Screenshot pixel differences"
                  className="max-h-52 w-full object-contain"
                />
              </div>
            ) : null}
          </CheckCard>

          <CheckCard
            icon={<Network className="h-[1.1rem] w-[1.1rem]" />}
            title="Network"
            ok={!network?.flagged}
            summary={
              network?.flagged
                ? 'Critical assets broke or disappeared after instrumentation.'
                : 'No broken or missing critical assets after instrumentation.'
            }>
            {network?.flagged ? (
              <div className="space-y-3">
                <div>
                  <p className="mb-1.5 text-[0.7rem] font-bold uppercase tracking-[0.06em] text-[#b42318]">
                    Broken after ({broken.length})
                  </p>
                  <IssueList
                    emptyLabel="None"
                    items={broken.map(item => (
                      <>
                        <span className="font-semibold text-[var(--inv-accent-hover)]">
                          {item.resource_type || 'asset'}
                        </span>
                        <span className="text-[var(--inv-text-muted)]"> · </span>
                        {item.url}
                        {item.status_before != null || item.status_after != null
                          ? ` (${item.status_before ?? '?'} → ${item.status_after ?? '?'})`
                          : null}
                      </>
                    ))}
                  />
                </div>
                <div>
                  <p className="mb-1.5 text-[0.7rem] font-bold uppercase tracking-[0.06em] text-[#b42318]">
                    Missing after ({missing.length})
                  </p>
                  <IssueList
                    emptyLabel="None"
                    items={missing.map(item => (
                      <>
                        <span className="font-semibold text-[var(--inv-accent-hover)]">
                          {item.resource_type || 'asset'}
                        </span>
                        <span className="text-[var(--inv-text-muted)]"> · </span>
                        {item.url}
                      </>
                    ))}
                  />
                </div>
              </div>
            ) : (
              <p className="m-0 rounded-[10px] bg-[var(--inv-surface-muted)] px-3 py-2 text-[0.78rem] text-[var(--inv-text-secondary)]">
                Render-critical scripts, styles, fonts, and images remained
                healthy across the capture window.
              </p>
            )}
          </CheckCard>

          <CheckCard
            icon={<Terminal className="h-[1.1rem] w-[1.1rem]" />}
            title="Console"
            ok={!consoleDiff?.flagged}
            summary={
              consoleDiff?.flagged
                ? 'New console errors or warnings appeared after instrumentation.'
                : 'No new console errors or warnings after instrumentation.'
            }>
            {consoleDiff?.flagged ? (
              <IssueList
                emptyLabel="None"
                items={consoleIssues.map((msg, i) => (
                  <span key={i} className="text-[#912018]">{msg}</span>
                ))}
              />
            ) : (
              <p className="m-0 rounded-[10px] bg-[var(--inv-surface-muted)] px-3 py-2 text-[0.78rem] text-[var(--inv-text-secondary)]">
                Browser console stayed clean relative to the before capture.
              </p>
            )}
          </CheckCard>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ComparisonDiffModal;
